import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../config/api';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSave, FaTimes } from 'react-icons/fa';
import './AdminEmailTemplates.css';

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    category: 'Autre',
    htmlContent: '',
    textContent: '',
    variables: []
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      alert('Erreur lors du chargement des modèles');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      category: 'Autre',
      htmlContent: '',
      textContent: '',
      variables: []
    });
    setShowEditor(true);
    setShowPreview(false);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      category: template.category,
      htmlContent: template.htmlContent,
      textContent: template.textContent || '',
      variables: template.variables || []
    });
    setShowEditor(true);
    setShowPreview(false);
  };

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.subject || !formData.htmlContent) {
        alert('Veuillez remplir tous les champs requis');
        return;
      }

      if (editingTemplate) {
        await api.put(`/email/templates/${editingTemplate._id}`, formData);
      } else {
        await api.post('/email/templates', formData);
      }

      alert('Modèle enregistré avec succès');
      setShowEditor(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Erreur lors de l\'enregistrement du modèle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce modèle?')) return;

    try {
      await api.delete(`/email/templates/${id}`);
      alert('Modèle supprimé avec succès');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Erreur lors de la suppression du modèle');
    }
  };

  const handleCancel = () => {
    setShowEditor(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      subject: '',
      category: 'Autre',
      htmlContent: '',
      textContent: '',
      variables: []
    });
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const extractVariables = (content) => {
    const regex = /\{(\w+)\}/g;
    const matches = content.matchAll(regex);
    const vars = new Set();
    for (const match of matches) {
      vars.add(match[1]);
    }
    return Array.from(vars);
  };

  const handleContentChange = (value) => {
    setFormData({
      ...formData,
      htmlContent: value,
      variables: extractVariables(value)
    });
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Chargement...</div></div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Modèles d'emails</h1>
        <button onClick={handleNewTemplate} className="btn-primary">
          <FaPlus /> Nouveau modèle
        </button>
      </div>

      {showEditor && (
        <div className="email-template-editor">
          <div className="editor-header">
            <h2>{editingTemplate ? 'Modifier le modèle' : 'Nouveau modèle'}</h2>
            <div className="editor-actions">
              <button onClick={togglePreview} className="btn-secondary">
                <FaEye /> {showPreview ? 'Masquer' : 'Aperçu'}
              </button>
              <button onClick={handleSave} className="btn-primary">
                <FaSave /> Enregistrer
              </button>
              <button onClick={handleCancel} className="btn-secondary">
                <FaTimes /> Annuler
              </button>
            </div>
          </div>

          <div className={`editor-content ${showPreview ? 'with-preview' : ''}`}>
            <div className="editor-form">
              <div className="form-group">
                <label>Nom du modèle *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Newsletter mensuelle"
                />
              </div>

              <div className="form-group">
                <label>Sujet de l'email *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Ex: Newsletter Oltre Frontiera - Janvier 2025"
                />
              </div>

              <div className="form-group">
                <label>Catégorie</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="Billetterie">Billetterie</option>
                  <option value="Communication">Communication</option>
                  <option value="Système">Système</option>
                  <option value="Paiement">Paiement</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div className="form-group">
                <label>Contenu HTML *</label>
                <div className="editor-help">
                  <small>Vous pouvez utiliser des variables comme {'{firstName}'}, {'{lastName}'}, etc.</small>
                </div>
                <ReactQuill
                  theme="snow"
                  value={formData.htmlContent}
                  onChange={handleContentChange}
                  placeholder="Rédigez votre email ici..."
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'align': [] }],
                      ['link', 'image'],
                      ['clean']
                    ]
                  }}
                  formats={[
                    'header',
                    'bold', 'italic', 'underline', 'strike',
                    'list', 'bullet',
                    'color', 'background',
                    'align',
                    'link', 'image'
                  ]}
                  style={{ minHeight: '400px', marginBottom: '50px' }}
                />
              </div>

              {formData.variables.length > 0 && (
                <div className="form-group">
                  <label>Variables détectées</label>
                  <div className="variables-list">
                    {formData.variables.map((variable, index) => (
                      <span key={index} className="variable-tag">
                        {'{'}{variable}{'}'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {showPreview && (
              <div className="preview-pane">
                <h3>Aperçu</h3>
                <div className="preview-content">
                  <div className="preview-subject">
                    <strong>Sujet:</strong> {formData.subject || '(Aucun sujet)'}
                  </div>
                  <div
                    className="preview-html"
                    dangerouslySetInnerHTML={{ __html: formData.htmlContent || '<p>Aucun contenu</p>' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="templates-list">
        {templates.length === 0 ? (
          <div className="empty-state">
            <p>Aucun modèle créé. Cliquez sur "Nouveau modèle" pour commencer.</p>
          </div>
        ) : (
          <div className="templates-grid">
            {templates.map((template) => (
              <div key={template._id} className="template-card">
                <div className="template-header">
                  <h3>{template.name}</h3>
                  <div className="template-actions">
                    <button onClick={() => handleEdit(template)} className="btn-icon" title="Modifier">
                      <FaEdit />
                    </button>
                    <button onClick={() => handleDelete(template._id)} className="btn-icon btn-danger" title="Supprimer">
                      <FaTrash />
                    </button>
                  </div>
                </div>
                <div className="template-info">
                  <p><strong>Sujet:</strong> {template.subject}</p>
                  <p><strong>Catégorie:</strong> {template.category}</p>
                  <p><strong>Créé:</strong> {new Date(template.createdAt).toLocaleDateString('fr-FR')}</p>
                  {template.variables && template.variables.length > 0 && (
                    <div className="template-variables">
                      <strong>Variables:</strong>
                      {template.variables.map((variable, index) => (
                        <span key={index} className="variable-tag-small">
                          {'{'}{variable}{'}'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
